// GUS-87: shared registry of endoscopy/proctology informed-consent form types.
// Every type shares the same "patient details" header and the same
// "declaration + signatures" footer (rendered by the consuming components) —
// only the body text (`body`) and the treatment-specific consent line(s)
// (`consentLines`) differ per type. Mirrored (kept in sync manually) at
// weblinks/src/app/consent-types.ts for the public remote-signing page.

export interface ConsentBlock {
  heading?: string;
  paragraphs?: string[];
  bullets?: string[];
}

export interface ConsentTypeDef {
  key: string;            // stored in USGConsents.ConsentType
  label: string;          // picker label (Hebrew)
  title: string;          // form / print / public-page title
  body: ConsentBlock[];   // per-type body content
  consentLines: string[]; // the "אני מסכים/ה..." line(s) of the declaration
}

export const CONSENT_TYPES: ConsentTypeDef[] = [
  {
    key: 'Endoscopy',
    label: 'אנדוסקופיה',
    title: 'טופס הסכמה מדעת לבדיקה אנדוסקופית',
    body: [
      {
        heading: 'הסבר על הבדיקה',
        paragraphs: [
          'אנדוסקופ הוא צינור גמיש המאפשר לראות ולהדגים את כל מערכת העיכול. בזמן הבדיקה ניתן, במידת הצורך, לבצע פעולות אבחון וטיפול כמו: הדמיית סונר, נטילת ביופסיות, כריתת פוליפים, צריבת נקודות דמם, טיפול בדליות הוושט, הוצאת גוף זר, הרחבת הצירויות במעי ועוד. אורכו של האנדוסקופ נע בין 1.20 לבין 1.80 מטר, קוטרו 1 ס"מ והוא משמש לבדיקת מערכת העיכול העליונה והתחתונה. כאשר מדובר בבדיקה של דרכי עיכול עליונות (EUS, אזופגוסקופיה, גסטרוסקופיה, אנטרוסקופיה), מוחדר האנדוסקופ דרך הפה. כאשר מדובר בבדיקה של דרכי עיכול תחתונות (סיגמואידוסקופיה, קולונוסקופיה רקטלי, אנטרוסקופי), מוחדר אנדוסקופ דרך פי הטבעת. במהלך הבדיקה, מוחדרים דרכו מכשירים בהתאם לפעולות הנדרשות. משך הבדיקה נע, בדרך כלל, בין 15 דקות לשעה ותלוי במבנה המעי ובפעולות הנדרשות לצורך אבחון וטיפול.',
          'על מנת לאפשר את הבדיקה בצורה המיטבית היא מתבצעת בטשטוש [סדציה] ע"י מתן תרופות הרגעה. השימוש בתרופות הרגעה עלול לגרום, לעיתים נדירות, להפרעות בנשימה ולהפרעות בפעילות הלב בעיקר אצל חולי לב ומערכת הנשימה. סיכון אפשרי נוסף הוא תגובה אלרגית לחומר המאלחש.',
          'במקרים בהם הבדיקה מתבצעת בסדציה, אסור לנהוג או לבצע פעילות הדורשת ערנות באותו היום.',
        ],
      },
      {
        paragraphs: [
          'אני מבין שקיימת אפשרות שתוך ביצוע הטיפול העיקרי או מיד לאחריו יתברר שיש צורך להרחיב את היקפו, לשנותו או לנקוט בהליכים אחרים או נוספים לצורך הצלת חיים או למניעת נזק גופני, לרבות פעולות כירורגיות שלא ניתן לצפותם כעת בוודאות או במלואם, אך משמעותם הובהרה לי.',
        ],
      },
      {
        heading: 'חלופות אבחנתיות וטיפוליות',
        paragraphs: [
          'הוסבר לי על קיומן של חלופות אבחנתיות וטיפוליות כמו צילום רנטגן או CT או וידאו קפסולה - כולל יתרונן, חסרונן, תופעות הלוואי שלהן והסיבוכים האפשריים. מקובל שהפעולה האנדוסקופית היא היעילה ביותר לאבחן ולטפל בפתולגיה של מערכת העיכול.',
        ],
      },
      {
        heading: 'מגבלות הבדיקה',
        paragraphs: [
          'הוסבר לי שלמרות שהבדיקה האנדוסקופית הינה הטובה ביותר בנמצא היום לגילוי נגעים בחלל המעי כמו פוליפים וגידולים, גם לבדיקה זו יש מגבלות: הבדיקה מסוגלת לאתר כ-90% מהפוליפים וכ-99% של השאתות הממאירות. באוכלוסייה בסיכון, אחוז הגילוי עלול להיות אף נמוך יותר.',
        ],
      },
      {
        heading: 'סיכונים ותופעות לוואי',
        paragraphs: [
          'הוסברו לי תופעות הלוואי של הטיפול העיקרי לרבות: כאב בטן, אספירציה [שאיפת רוק או נוזל הקיבה לריאות], אי נוחות ותחושה של נפיחות בבטן. הוסברו לי הסיבוכים המורכבים ונדירים האפשריים, לרבות: דימום או קרע של דופן מערכת העיכול, אשר בחלק מהמקרים יצריכו תיקון ניתוחי. בבדיקת דרכי עיכול עליונות עלול להיגרם נזק לשיניים עקב החדרת המכשיר דרך הפה. כמו כן, עלולה להופיע דלקת בווריד בו ניתן העירוי.',
          'הסיבוכים האמורים אינם שכיחים. במקרים בהם מתבצע הליך טיפולי כמו: ביופסיה, כריתת פוליפ, הרחבה ואחרים השכיחות גבוהה יותר.',
        ],
      },
    ],
    consentLines: ['אני מסכים/ה מרצוני החופשי לביצוע בדיקה וטיפול אנדוסקופי של מערכת העיכול.'],
  },
  {
    key: 'eXroid',
    label: 'טחורים - eXroid',
    title: 'טופס הסכמה מדעת לטיפול טחורים בשיטת אקסרואיד [eXroid]',
    body: [
      {
        heading: 'הסבר על הטיפול',
        paragraphs: [
          'טחורים הם כלי דם נפוחים באזור פי הטבעת ועלולים לגרום לכאב, לגירוד, לאי-נוחות ולדימום בזמן או אחרי יציאות. לפני החלטה על הטיפול מתבצעת בדיקה אבחנתית ע"י רופא מומחה להערכת המחלה והחלטה לגבי הטיפול המתאים.',
          'טיפול בטחורים בשיטת eXroid משתמש בזרם חשמלי חלש וממוקד הגורם לכיווץ הטחור בבטחה עד להיעלמותו.',
        ],
      },
      {
        heading: 'היתרונות הבולטים של הטיפול הם',
        bullets: [
          'משך הטיפול קצר [עד שעה כולל הבדיקה לפני הטיפול].',
          'אין צורך בהכנה מוקדמת.',
          'הטיפול אינו כרוך בכאב אך לעיתים מלווה בתחושת אי נוחות ולכן לא נדרשת הרדמה. במקרים מסוימים ובהתאם לצורך הרפואי משתמשים באלחוש מקומי.',
          'חזרה מיידית לשגרה במרבית המקרים.',
        ],
      },
      {
        heading: 'אלטרנטיבות לטיפול',
        paragraphs: ['טיפולים ניתוחיים:'],
        bullets: [
          'ניתוח לכריתת טחורים - כריתה של מרבית רקמת הטחורים באמצעות אנרגיה.',
          'ניתוח לקשירה העורק המזין את הטחור [HAL] ע"י זיהוי העורק במכשיר אולטרסאונד דופלר.',
          'ניתוח לקיבוע הטחורים [PPH], מתבצע באמצעות מכשיר סיכות ואינו כורת את הטחור אלא משיב אותו למקומו התקין בתעלה האנאלית.',
        ],
      },
      {
        paragraphs: ['טיפולים מקומיים:'],
        bullets: [
          'קשירה בגומיות: הנחת גומייה לבסיס הטחור לעצירת אספקת הדם עד שהוא נושר.',
          'הטרשה [סקלרותרפיה]: הזרקת חומר כימי לכיווץ כלי הדם והצטלקות הרקמה.',
          'קואגולציה: צריבת הרקמה באמצעות לייזר, אינפרא-אדום או אנרגיה חשמלית.',
        ],
      },
      {
        heading: 'מגבלות הבדיקה',
        bullets: [
          'הטיפול מיועד לטחורים פנימיים בלבד.',
          'בחולים עם קוצב לב או דפיברילטור נדרש אישור מקרדיולוג לפני הטיפול.',
          'בהריון הטיפול אסור.',
          'בעיות קרישה ודימום, כולל במטופלים הנוטלים מדללי דם ברמת INR>3, נדרש אישור ממומחה לקרישה.',
          'מחלות מעי דלקתיות מהוות מניעה יחסית לטיפול.',
          'זיהום פעיל או חשד לגידול סרטני מחייבים בירור לפני ביצוע ההליך.',
          'הצלחת הטיפול תלויה בגודל ובמספר הטחורים ולעיתים נדרש יותר מטיפול אחד. יש לציין כי בחמישה אחוז מהמקרים אין שינוי במצב הטחורים לאחר הפעולה.',
        ],
      },
      {
        heading: 'סיבוכים ותופעות לוואי',
        paragraphs: [
          'תופעות הלוואי המקובלות הן אי נוחות, תחושת לחץ ודימום בזמן הבדיקה. התופעות קלות וחולפות. לאחר הבדיקה יכולה להיות נפיחות מקומית. לעיתים נדירות עלול להתפתח אבסס.',
          'הוסבר לי שמדובר בבדיקה בטוחה והסיבוכים משמעותיים ונדירים.',
        ],
      },
    ],
    consentLines: ['אני מסכים/ה מרצוני החופשי לביצוע טיפול בטחורים בשיטת האקסרואיד [Exroid].'],
  },
  {
    key: 'Fissure',
    label: 'פיסורה',
    title: 'טופס הסכמה מדעת לטיפול בסדק [פיסורה] בפי הטבעת',
    body: [
      {
        heading: 'הסבר על המחלה והטיפול',
        paragraphs: [
          'פיסורה היא קרע או סדק קטן ברירית העדינה שמצפה את פי הטבעת. היא נגרמת בדרך כלל ממעבר של צואה קשה, ומתבטאת בכאבים עזים ודימום בזמן יציאות. הפצע גורם לכאב חזק ולכיווץ השרירים באזור ועקב כך לירידה בזרימת הדם לאזור ולהפרעה ברפוי. פיסורה שאינה מתרפאת תוך 6-8 שבועות נחשבת לכרונית ב-40-50% מהמקרים ומצריכה טיפול מיוחד.',
          'מטרת הטיפול היא להקטין את עוצמת הכיווץ של סוגרי פי הטבעת על מנת להקל על הכאבים ולאפשר ריפוי הסדק/קרע ברירית פי הטבעת.',
        ],
      },
      {
        heading: 'האלטרנטיבות לטיפול',
        paragraphs: [
          'ניתן להקטין את עוצמת הכיווץ במספר דרכים: הרחבה ידנית של שריר סוגר פי הטבעת, הזרקת בוטוקס, חיתוך (בניתוח) של שריר הסוגר הפנימי. ההחלטה על הטיפול הרפואי מתבצעת על פי שיקול דעת רפואי ובהתאם למצב הרפואי.',
          'הבוטוקס הוא חומר משתק שרירים, הפועל על ידי חסימת הקשר בין השריר לעצב שמעצבב אותו. לאחר הזרקת בוטוקס, נדרשת הפעלה חוזרת ונשנית של השריר כדי לגרום לחסימת הקשר בין השריר לעצב ולכן התועלת מורגשת לרוב רק לאחר מס\' ימים. החומר נשאר ברקמה כ-3-4 חודשים ובהדרגה מתפרק ופעילותו פוסקת. יחד עם זאת, מטרת ההזרקה היא לשתק את השריר באופן זמני ולהביא בזמן זה לריפוי הפיסורה, ולרוב לא מבצעים הזרקות חוזרות ונשנות של החומר כל מספר חודשים.',
        ],
      },
      {
        heading: 'מגבלות הטיפול',
        paragraphs: [
          'שיעור ההצלחה של הזרקת בוטוקס בריפוי פיסורה מוערך בכ-60-70% מהמקרים, ובמקרים בהם הטיפול נכשל צריך לשקול טיפול ניתוחי.',
          'תופעות הלוואי הצפויות הן: כאב מקומי, אי נוחות ועצירות.',
          'הפעולה נחשבת כבטוחה אבל קיימים סיבוכים אפשריים לרבות דימום, זיהום עד כדי התפתחות מורסה. לעיתים מופיעה הפרעה זמנית בשליטה בסוגרים שמתבטאת בפליטת גזים והכתמת התחתונים בריר/צואה. רוב התופעות חולפות ספונטנית או ע"י ביצוע פיזיותרפיה לרצפת האגן.',
        ],
      },
    ],
    consentLines: ['אני מסכים/ה מרצוני החופשי לביצוע טיפול בהזרקת בוטוקס לטיפול בפיסורה.'],
  },
  {
    key: 'HRA',
    label: 'אנוסקופיה ברזולוציה גבוהה (HRA)',
    title: 'טופס הסכמה מדעת לביצוע אנוסקופיה ברזולוציה גבוהה (HRA)',
    body: [
      {
        heading: 'הסבר על הפעולה',
        paragraphs: [
          'הוסבר לי כי אנוסקופיה ברזולוציה גבוהה (HRA) היא בדיקה המאפשרת הסתכלות מפורטת על תעלת פי הטבעת והאזור הפריאנלי באמצעות מכשיר הגדלה ייעודי, לאחר שימוש בתמיסות המסייעות בזיהוי אזורים חשודים (כגון חומצה אצטית ולוגול, לפי שיקול דעת הרופא).',
          'מטרת הבדיקה היא לאתר נגעים הקשורים לנגיף ה-HPV, שינויים טרום-סרטניים (AIN/HSIL), סרטן בשלב מוקדם או ממצאים אחרים הדורשים אבחון או טיפול.',
          'במהלך הבדיקה, ובהתאם לממצאים, ייתכן שתילקח ביופסיה אחת או יותר מאזורים החשודים כמחייבים אבחון פתולוגי.',
        ],
      },
      {
        heading: 'סיכונים וסיבוכים אפשריים',
        paragraphs: ['הוסבר לי כי הבדיקה נחשבת בטוחה, אולם עלולים להופיע סיבוכים או תופעות לוואי, ובהם:'],
        bullets: [
          'אי נוחות או כאב במהלך הבדיקה או לאחריה.',
          'זיהום (נדיר).',
          'תגובה מקומית לתמיסות המשמשות במהלך הבדיקה.',
          'עילפון או תחושת חולשה (נדיר).',
        ],
      },
      {
        heading: 'מגבלות הבדיקה',
        paragraphs: [
          'לעיתים הבדיקה אינה מאפשרת אבחנה חד-משמעית, וייתכן שיהיה צורך בבדיקות נוספות או בבדיקה חוזרת. הוסבר לי כי אין בדיקה בעלת דיוק של 100%, וייתכן שקיימים נגעים שלא יאותרו למרות ביצוע הבדיקה.',
        ],
      },
      {
        heading: 'תופעות לוואי וסיבוכים',
        paragraphs: [
          'הוסבר לי כי ייתכנו אי נוחות קלה או דימום בכמות קטנה, במיוחד אם נלקחו ביופסיות.',
          'במקרה של אחד מהבאים עליי לפנות לבדיקה רפואית:',
        ],
        bullets: [
          'דימום משמעותי או מתמשך.',
          'חום.',
          'כאב חזק שאינו משתפר.',
          'הפרשה מוגלתית.',
          'קושי במתן שתן או ביציאות שאינו חולף.',
        ],
      },
      {
        paragraphs: [
          'אם נלקחו ביופסיות, תוצאותיהן יתקבלו לאחר מספר ימים עד שבועות, ובהתאם להן ייקבע המשך המעקב או הטיפול.',
        ],
      },
    ],
    consentLines: [
      'ידוע לי כי ניתן להפסיק את הבדיקה בכל שלב, ככל שהדבר אפשרי מבחינה רפואית.',
      'אני מסכים/ה לביצוע אנוסקופיה ברזולוציה גבוהה (HRA).',
      'אני מסכים/ה כי במידת הצורך ועל פי שיקול דעת רפואי יילקחו ביופסיות מאזורים חשודים במהלך הבדיקה.',
    ],
  },
  {
    key: 'AnalPap',
    label: 'משטח ציטולוגי אנאלי (Anal Pap Test)',
    title: 'טופס הסכמה מדעת לביצוע משטח ציטולוגי בפי הטבעת (Anal Pap Test)',
    body: [
      {
        heading: 'הסבר על הבדיקה',
        paragraphs: [
          'הוסבר לי כי משטח ציטולוגי בפי הטבעת (Anal Pap Test) הוא בדיקת סקר המיועדת לאיתור מוקדם של שינויים טרום-סרטניים או סרטניים בתאי תעלת פי הטבעת, בעיקר שינויים הקשורים לזיהום בנגיף הפפילומה האנושי (HPV).',
          'הבדיקה מתבצעת באמצעות החדרת מטוש עדין לתעלת פי הטבעת וסיבובו לצורך איסוף תאים לבדיקה ציטולוגית במעבדה. הבדיקה אורכת מספר שניות ואינה מצריכה הרדמה או אלחוש.',
          'הוסבר לי כי במקרים מסוימים ניתן יהיה לבצע במקביל גם בדיקה פתולוגית של זני וירוס הפפילומה (HPV) מאותה הדגימה, בהתאם להתוויה הרפואית ולשיקול דעת הצוות המטפל.',
        ],
      },
      {
        heading: 'מטרת הבדיקה',
        paragraphs: [
          'מטרת הבדיקה היא לסייע באיתור מוקדם של שינויים תאיים העלולים להתפתח לנגעים טרום-סרטניים או לסרטן פי הטבעת, ובכך לאפשר המשך בירור או טיפול במידת הצורך.',
        ],
      },
      {
        heading: 'מגבלות הבדיקה',
        paragraphs: ['הוסבר לי כי:'],
        bullets: [
          'הבדיקה היא בדיקת סקר ואינה מאבחנת בוודאות נגעים טרום-סרטניים או ממאירים.',
          'תוצאה תקינה אינה שוללת לחלוטין קיומם של נגעים.',
          'לעיתים הדגימה אינה מספקת לצורך פענוח, ויידרש לבצע את הבדיקה מחדש.',
          'במקרה של תוצאה חריגה ייתכן שיומלץ על בירור נוסף, לרבות אנוסקופיה ברזולוציה גבוהה (HRA) עם או ללא ביופסיה.',
        ],
      },
      {
        heading: 'סיכונים ותופעות לוואי',
        paragraphs: [
          'הוסבר לי כי מדובר בבדיקה בטוחה מאוד, סיבוכים משמעותיים כתוצאה מהבדיקה נדירים ביותר. אבל בכל מקרה ייתכנו:',
        ],
        bullets: [
          'אי-נוחות קלה במהלך הבדיקה.',
          'תחושת לחץ או צורך ביציאה.',
          'דימום קל וחולף, בעיקר במטופלים עם טחורים או רגישות מקומית.',
        ],
      },
    ],
    consentLines: ['אני מסכים/ה מרצוני החופשי לביצוע משטח ציטולוגי אנאלי (Anal Pap Test).'],
  },
];

export function resolveConsentTypeKey(key: string): string {
  return key || 'Endoscopy';
}

export function resolveConsentType(key: string): ConsentTypeDef {
  const resolvedKey = resolveConsentTypeKey(key);
  return CONSENT_TYPES.find(t => t.key === resolvedKey) || CONSENT_TYPES[0];
}
